import { OCEAN_STATUS_VALUES } from './workflow-ui.mjs?v=20260806.100';
import { containerStatusLabel } from './container-status-presentation.mjs?v=20260806.100';
import { fieldOptions } from './container-field-registry.mjs?v=20260806.100';

const OCEAN_STATUS_OPTIONS = Object.freeze(
  OCEAN_STATUS_VALUES.map((value) => [value, containerStatusLabel(value)]),
);

/**
 * The dates worth filtering and sorting on - the same ones the default status
 * order is judged by (Alan, 2026-08-08). Choosing one here does two things: it
 * bounds the rows, and it takes over the sort. "Filter 有他的特定功能, 由最高的
 * 权限去排序表格" - a chosen timeline outranks the per-status default, because
 * asking to see a date range is asking to read the table in that date's order.
 */
export const TIMELINE_FIELDS = Object.freeze([
  ['eta_at', 'ETA'],
  ['lfd_at', 'LFD'],
  ['pickup_appointment_at', '提柜预约 Pickup Appointment'],
  ['reported_empty_at', '报空时间 Reported Empty'],
  ['actual_empty_returned_at', '还空时间 Empty Returned'],
]);

function escapeHtml(value) {
  return String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

/**
 * What the panel offers (Alan, 2026-08-08).
 *
 * It used to ask for CTF, TMF, Customer, Terminal and Driver, the last three as
 * free text - you had to already know how a value was spelled to filter on it,
 * and a near miss silently returned nothing. What is actually needed is the
 * shipping line and the terminal, chosen from the values the table really holds,
 * and a date range on ETA.
 *
 * The choices come from the loaded rows rather than from a fixed list: these are
 * imported values with no catalog behind them, so the only honest source of
 * "which terminals exist" is the terminals in front of you.
 */
export function dashboardFilterDefinitions(columns) {
  const visible = Array.isArray(columns) ? columns : [];
  const visibleKeys = new Set(visible.map(({ key }) => key));
  const dateFields = visible
    .filter(({ format }) => ['date', 'date-only', 'date-time'].includes(format))
    .map(({ key, label }) => ({ key, label }));
  const fields = [];
  if (visibleKeys.has('ocean_status')) fields.push({ key: 'ocean_status', label: 'Ocean Status' });
  if (visibleKeys.has('ssl')) fields.push({ key: 'ssl', label: '船司 Shipping Line' });
  if (visibleKeys.has('terminal_name')) fields.push({ key: 'terminal_name', label: '码头 Terminal' });
  return { dateFields, fields };
}

/**
 * The distinct values a column holds across the loaded rows, sorted for reading.
 * Blank cells are not a choice, and the comparison is case-insensitive so one
 * terminal spelled two ways does not become two options.
 */
export function distinctColumnValues(rows, key) {
  const seen = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const value = String(row?.[key] ?? '').trim();
    if (!value) continue;
    const fold = value.toUpperCase();
    if (!seen.has(fold)) seen.set(fold, value);
  }
  return [...seen.values()].sort((left, right) => left.localeCompare(right, 'en', { sensitivity: 'base' }));
}

/**
 * A menu's choices: the fleet's values, not only the rows on screen (Alan, 2026-10-17: with ITS chosen, the
 * terminal menu offered ITS alone, and switching meant going back to Any first). The loaded rows are already the
 * filtered rows, so on their own they shrink to the choice just made. fieldOptions is the seeded list plus every
 * value the whole fleet holds (counted server-side on the summary walk); the loaded rows and the current choice are
 * added so nothing on screen is missing from its own menu.
 */
// Status / shipping line / terminal combinations from the summary (functions/src/dashboard-query.ts
// summarizeFilterFacets). Empty until the first summary arrives; then each menu offers only what the panel's
// other choices leave (Alan, 2026-10-17: "点击pending后请只显示有的码头其他的不显示").
let FILTER_FACETS = [];
export function setFilterFacets(facets) {
  FILTER_FACETS = Array.isArray(facets)
    ? facets.filter((tuple) => Array.isArray(tuple) && tuple.length === 3).map((tuple) => tuple.map((value) => String(value ?? '')))
    : [];
}

const FACET_INDEX = Object.freeze({ ssl: 1, terminal_name: 2 });

/**
 * The menu for `key` narrowed by the other choices on the panel - the ticked statuses and the other menu - never
 * by its own value, so a chosen terminal still lists every terminal the statuses allow. Null without facets,
 * and the caller falls back to the fleet list.
 */
export function facetChoices(key, { statuses = [], ssl = '', terminal_name: terminal = '' } = {}, facets = FILTER_FACETS) {
  if (!Array.isArray(facets) || !facets.length || !(key in FACET_INDEX)) return null;
  const wantStatuses = new Set((statuses || []).map(String));
  const otherKey = key === 'ssl' ? 'terminal_name' : 'ssl';
  const otherValue = String((otherKey === 'ssl' ? ssl : terminal) || '').trim().toUpperCase();
  const seen = new Map();
  for (const tuple of facets) {
    if (wantStatuses.size && !wantStatuses.has(tuple[0])) continue;
    // The server matches these two by "contains", so the panel does too.
    if (otherValue && !String(tuple[FACET_INDEX[otherKey]]).toUpperCase().includes(otherValue)) continue;
    const value = String(tuple[FACET_INDEX[key]]).trim();
    if (value && !seen.has(value.toUpperCase())) seen.set(value.toUpperCase(), value);
  }
  return [...seen.values()].sort((left, right) => left.localeCompare(right, 'en', { sensitivity: 'base' }));
}

export function filterChoices(rows, key, columns, selected = '') {
  const column = (Array.isArray(columns) ? columns : []).find((entry) => entry?.key === key);
  const fleet = column ? fieldOptions(column).filter((value) => value !== 'CUSTOM') : [];
  const extra = [...fleet, selected].filter(Boolean).map((value) => ({ [key]: value }));
  return distinctColumnValues([...(Array.isArray(rows) ? rows : []), ...extra], key);
}

function valueSelectMarkup(name, label, values, selected) {
  const options = values
    .map((value) => `<option value="${escapeHtml(value)}" ${String(selected ?? '').toUpperCase() === value.toUpperCase() ? 'selected' : ''}>${escapeHtml(value)}</option>`)
    .join('');
  return `<label><span>${escapeHtml(label)}</span><select name="${escapeHtml(name)}"><option value="">Any</option>${options}</select></label>`;
}

/**
 * Ocean Status is the filter (Alan, 2026-08-08, "filter 过滤最高权限"). Everything
 * else the panel used to ask for is gone: CTF, TMF, Customer and Driver, and the
 * generic date-field chooser. What is left narrows on what the operation actually
 * sorts and searches by - the nine statuses, the shipping line, the terminal, and
 * a range on ETA.
 */
export function mountDashboardFilterPopover({
  trigger, overlayManager, columns, initial = {}, onApply, rows = () => [],
  // Statuses not to offer right now. The split's left pane never shows a reported
  // empty - they are the right pane's - so ticking 已报空 there could only empty it.
  excludeStatuses = () => [],
}) {
  if (!trigger || !overlayManager || typeof overlayManager.open !== 'function'
    || typeof onApply !== 'function') {
    throw new TypeError('Dashboard Filter options are invalid.');
  }
  const documentRef = trigger.ownerDocument;
  const definitions = dashboardFilterDefinitions(columns);
  const has = (key) => definitions.fields.some((field) => field.key === key);
  const cleared = {
    dateField: 'eta_at', statuses: [], ssl: '', terminal_name: '',
    ctfStatus: '', tmfStatus: '', customer_name: '', driver_name: '',
  };

  function open() {
    const panel = documentRef.createElement('div');
    panel.className = 'dashboard-filter-popover';
    const statuses = new Set(Array.isArray(initial.statuses) ? initial.statuses : []);
    const excluded = new Set(excludeStatuses() ?? []);
    const offered = OCEAN_STATUS_OPTIONS.filter(([value]) => !excluded.has(value));
    const loaded = rows();
    panel.innerHTML = `
      <form class="dashboard-filter-form">
        <div class="dashboard-filter-form__heading"><strong>Filters</strong></div>
        ${has('ocean_status') ? `<fieldset><legend>Ocean Status</legend><div class="dashboard-filter-statuses">${offered.map(([value, label]) => `<label><input type="checkbox" name="statuses" value="${escapeHtml(value)}" ${statuses.has(value) ? 'checked' : ''}><span>${escapeHtml(label)}</span></label>`).join('')}</div></fieldset>` : ''}
        ${has('ssl') ? valueSelectMarkup('ssl', '船司 Shipping Line', filterChoices(loaded, 'ssl', columns, initial.ssl), initial.ssl) : ''}
        ${has('terminal_name') ? valueSelectMarkup('terminal_name', '码头 Terminal', filterChoices(loaded, 'terminal_name', columns, initial.terminal_name), initial.terminal_name) : ''}
        <fieldset><legend>时间线 Timeline</legend>
          <label><span>Date</span><select name="dateField">${TIMELINE_FIELDS.map(([key, label]) => `<option value="${escapeHtml(key)}" ${(initial.dateField || 'eta_at') === key ? 'selected' : ''}>${escapeHtml(label)}</option>`).join('')}</select></label>
          <div class="dashboard-filter-range">
            <label><span>From</span><input name="from" type="date" value="${escapeHtml(initial.from || '')}"></label>
            <label><span>To</span><input name="to" type="date" value="${escapeHtml(initial.to || '')}"></label>
          </div>
        </fieldset>
        <div class="dashboard-filter-form__actions"><button type="reset" class="secondary">Clear</button><button type="submit">Apply</button></div>
      </form>`;
    const overlay = overlayManager.open({
      anchor: trigger,
      content: panel,
      modal: false,
      kind: 'filter',
      role: 'dialog',
      ariaLabel: 'Filters',
    });
    const form = overlay.querySelector?.('form') || panel.querySelector?.('form');
    // Each menu follows the other choices as they are made, before Apply: tick pending and the Terminal menu
    // shrinks to pending's terminals at once. A chosen value always stays in its own menu.
    const narrowMenus = () => {
      if (!form) return;
      const data = new FormData(form);
      const choices = {
        statuses: data.getAll('statuses').map(String),
        ssl: String(data.get('ssl') || ''),
        terminal_name: String(data.get('terminal_name') || ''),
      };
      for (const key of ['ssl', 'terminal_name']) {
        const select = form.elements.namedItem(key);
        if (!select || !('options' in select)) continue;
        const narrowed = facetChoices(key, choices);
        if (!narrowed) continue;
        const current = select.value;
        const values = [...narrowed];
        if (current && !values.some((value) => value.toUpperCase() === current.toUpperCase())) values.unshift(current);
        select.innerHTML = `<option value="">Any</option>${values.map((value) => `<option value="${escapeHtml(value)}"${value.toUpperCase() === current.toUpperCase() ? ' selected' : ''}>${escapeHtml(value)}</option>`).join('')}`;
      }
    };
    narrowMenus();
    form?.addEventListener('change', (event) => {
      if (event.target?.name === 'statuses' || event.target?.name === 'ssl' || event.target?.name === 'terminal_name') narrowMenus();
    });
    form?.addEventListener('reset', (event) => {
      event.preventDefault();
      form.querySelectorAll('input[name="statuses"]').forEach((control) => {
        control.checked = false;
      });
      for (const name of ['ssl', 'terminal_name', 'from', 'to']) {
        const control = form.elements.namedItem(name);
        if (control && 'value' in control) control.value = '';
      }
      const dateControl = form.elements.namedItem('dateField');
      if (dateControl && 'value' in dateControl) dateControl.value = 'eta_at';
      narrowMenus();
      onApply({ ...cleared, from: '', to: '' });
    });
    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const data = new FormData(form);
      onApply({
        ...cleared,
        statuses: data.getAll('statuses').map(String),
        ssl: String(data.get('ssl') || '').trim(),
        terminal_name: String(data.get('terminal_name') || '').trim(),
        dateField: String(data.get('dateField') || 'eta_at'),
        from: String(data.get('from') || ''),
        to: String(data.get('to') || ''),
      });
      overlayManager.close('apply');
    });
  }

  trigger.addEventListener('click', open);
  return {
    open,
    destroy() {
      trigger.removeEventListener('click', open);
      overlayManager.close('destroy');
    },
  };
}
